import { useEffect, useRef, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import "./index.css"
import splash_image from '../../assets/images/hogcloud-site-splash.webp';
import arkieFrontImage from '../../assets/images/arkiebyte/arkiebyte-front.png';
import arkieFrontSparklesImage from '../../assets/images/arkiebyte/arkiebyte-front-sparkles.png';
import arkieSideImage from '../../assets/images/arkiebyte/arkiebyte-side.png';
import arkieSurprisedImage from '../../assets/images/arkiebyte/arkiebyte-surprised.png';
import arkieThinkingImage from '../../assets/images/arkiebyte/arkiebyte-thinking.png';
import serviceIcon3dPrint from '../../assets/images/service-icon-3dprint.webp';
import serviceIconApps from '../../assets/images/service-icon-apps.webp';
import serviceIconConsulting from '../../assets/images/service-icon-consulting.webp';
import serviceIconWebsite from '../../assets/images/service-icon-website.webp';
import {
  getHogcloudChatReply,
  hogcloudChatIntro,
  hogcloudChatQuickPrompts,
  type HogcloudChatPose,
} from './chatbotContent';

const offerings = [
  { title: "Custom 3D prints", icon: serviceIcon3dPrint, path: "/services/3dprints" },
  { title: "Websites", icon: serviceIconWebsite, path: "/services/websites" },
  { title: "App development", icon: serviceIconApps, path: "/services/appdev" },
  { title: "Tech consulting", icon: serviceIconConsulting, path: "/services/consulting" },
];

const chatbotPoseImages: Record<HogcloudChatPose, string> = {
  neutral: arkieFrontImage,
  focused: arkieSideImage,
  thinking: arkieThinkingImage,
  helpful: arkieSideImage,
  surprised: arkieSurprisedImage,
  celebrate: arkieFrontSparklesImage,
};

type ChatMessage = {
  id: number;
  sender: 'bot' | 'visitor';
  content: ReactNode;
  htmlContent?: string;
};

const Home = () => {
    const location = useLocation();
    const chatMessagesEndRef = useRef<HTMLDivElement | null>(null);
    const [selectedOffering, setSelectedOffering] = useState<string | null>(null);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
        { id: 1, sender: 'bot', content: hogcloudChatIntro.content },
    ]);
    const [chatInput, setChatInput] = useState('');
    const [chatAvatarPose, setChatAvatarPose] = useState<HogcloudChatPose>(hogcloudChatIntro.pose);
    const [isChatWaiting, setIsChatWaiting] = useState(false);
    const chatbotApiUrl = process.env.REACT_APP_HOGCLOUD_CHATBOT_API_URL;

    useEffect(() => {
        if (location.hash !== '#arkiebyte') {
            return;
        }

        setIsChatOpen(true);
        window.requestAnimationFrame(() => {
            document.getElementById('arkiebyte')?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        });
    }, [location.hash]);

    useEffect(() => {
        if (!isChatOpen) {
            return;
        }

        chatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [chatMessages, isChatOpen]);

    const appendChatMessage = (
        sender: ChatMessage['sender'],
        content: ReactNode,
        htmlContent?: string,
    ) => {
        setChatMessages((current) => [
            ...current,
            {
                id: Date.now() + Math.random(),
                sender,
                content,
                htmlContent,
            },
        ]);
    };

    const getChatbotApiReply = async (message: string): Promise<string | null> => {
        if (!chatbotApiUrl) {
            return null;
        }

        const response = await fetch(chatbotApiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ message }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(payload.message || 'Chatbot request failed.');
        }
        return typeof payload.answer === 'string' ? payload.answer : null;
    };

    const sendChatMessage = async (message: string) => {
        const trimmedMessage = message.trim();
        if (!trimmedMessage || isChatWaiting) {
            return;
        }

        if (['exit', 'bye', 'goodbye', 'close'].includes(trimmedMessage.toLowerCase())) {
            setChatInput('');
            setIsChatOpen(false);
            return;
        }

        appendChatMessage('visitor', trimmedMessage);
        setChatInput('');
        setIsChatWaiting(true);
        setChatAvatarPose('thinking');

        try {
            const apiReply = await getChatbotApiReply(trimmedMessage);
            if (apiReply) {
                appendChatMessage('bot', null, apiReply);
                setChatAvatarPose('helpful');
                return;
            }
            const botReply = getHogcloudChatReply(trimmedMessage);
            appendChatMessage('bot', botReply.content);
            setChatAvatarPose(botReply.pose);
        } catch (error) {
            const botReply = getHogcloudChatReply(trimmedMessage);
            appendChatMessage('bot', botReply.content);
            setChatAvatarPose(botReply.pose);
        } finally {
            setIsChatWaiting(false);
        }
    };

    const handleChatSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        sendChatMessage(chatInput);
    };

    return <>

            <div className="cards-container-home">
            <div className="hero-image-wrap">
                <Link to="/services" aria-label="View Hogcloud services">
                    <img src={splash_image} alt="Hogcloud hero" className="splash-image" />
                </Link>
            </div>

                <section className="home-offerings-section" aria-labelledby="home-offerings-title">
                    <h2 id="home-offerings-title">From Plastic to Pixels ...</h2>
                    <div className="home-offerings" aria-label="Hogcloud services">
                        {offerings.map((offering) => (
                            <div
                                key={offering.title}
                                className={`home-offering ${selectedOffering === offering.title ? 'active' : ''}`}
                            >
                                <button
                                    type="button"
                                    className="home-offering-select"
                                    onClick={() => setSelectedOffering(offering.title)}
                                >
                                    <img src={offering.icon} alt="" aria-hidden="true" />
                                    <span className="home-offering-title">{offering.title}</span>
                                </button>
                                {selectedOffering === offering.title && (
                                    <Link className="home-offering-link" to={offering.path}>
                                        More Info
                                    </Link>
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="home-chatbot-start" id="arkiebyte">
                        <img src={arkieFrontSparklesImage} alt="" aria-hidden="true" />
                        <div>
                            <p>Have a quick question?</p>
                            <button
                                type="button"
                                className="home-chatbot-start__button"
                                onClick={() => setIsChatOpen(true)}
                            >
                                Chat with ArkieByte
                            </button>
                        </div>
                    </div>
                </section>
            </div>

            <div className="leave-message" aria-live="polite">
                {isChatOpen && (
                    <section className="leave-message__panel" aria-labelledby="leave-message-title">
                        <div className="leave-message__header">
                            <div className="chatbot__header-intro">
                                <img
                                    className={`chatbot__avatar ${chatInput ? 'chatbot__avatar--typing' : ''}`}
                                    src={chatbotPoseImages[chatAvatarPose]}
                                    alt=""
                                    aria-hidden="true"
                                />
                                <div>
                                    <p className="chatbot__eyebrow" id="leave-message-title">Hogcloud Chat</p>
                                    <p>Ask me anything quick.</p>
                                </div>
                            </div>
                            <button
                                className="leave-message__close"
                                type="button"
                                aria-label="Close chat"
                                onClick={() => setIsChatOpen(false)}
                            >
                                ×
                            </button>
                        </div>

                        <div className="chatbot__messages" aria-label="Chat conversation">
                            {chatMessages.map((message) => (
                                message.htmlContent ? (
                                    <div
                                        className={`chatbot__message chatbot__message--${message.sender}`}
                                        dangerouslySetInnerHTML={{ __html: message.htmlContent }}
                                        key={message.id}
                                    />
                                ) : (
                                    <div className={`chatbot__message chatbot__message--${message.sender}`} key={message.id}>
                                        {message.content}
                                    </div>
                                )
                            ))}
                            {isChatWaiting && (
                                <div className="chatbot__message chatbot__message--bot">
                                    Checking Hogcloud docs...
                                </div>
                            )}
                            <div ref={chatMessagesEndRef} />
                        </div>

                        <div className="chatbot__quick-actions" aria-label="Quick chat options">
                            {hogcloudChatQuickPrompts.map((prompt) => (
                                <button
                                    type="button"
                                    key={prompt.message}
                                    disabled={isChatWaiting}
                                    onClick={() => sendChatMessage(prompt.message)}
                                >
                                    {prompt.label}
                                </button>
                            ))}
                        </div>

                        <form className="chatbot__input-form" onSubmit={handleChatSubmit}>
                            <label htmlFor="chatbot-input" className="chatbot__input-label">
                                Ask a question
                            </label>
                            <div className="chatbot__input-row">
                                <input
                                    id="chatbot-input"
                                    type="text"
                                    value={chatInput}
                                    placeholder="Ask about websites, apps, 3D prints..."
                                    disabled={isChatWaiting}
                                    onFocus={() => setChatAvatarPose('focused')}
                                    onChange={(event) => {
                                        setChatInput(event.target.value);
                                        setChatAvatarPose(event.target.value ? 'thinking' : hogcloudChatIntro.pose);
                                    }}
                                />
                                <button className="chatbot__send-button" type="submit" disabled={isChatWaiting}>
                                    Send
                                </button>
                            </div>
                        </form>
                    </section>
                )}

                {!isChatOpen && (
                    <button
                        className="leave-message__bubble"
                        type="button"
                        aria-expanded={isChatOpen}
                        onClick={() => setIsChatOpen(true)}
                    >
                        Chat With Us
                    </button>
                )}
            </div>
            

    
    
    </>;
  
    };


export default Home;
